import { setProperties } from '@ember/object';
import { getOwner } from '@ember/owner';
import { trackedObject } from '@ember/reactive/collections';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import dayjs from 'dayjs';
import type { Task, TaskInstance } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import type { Roomtype } from 'frontend-utils/models';
import type { Platform } from 'frontend-utils/utils/constants/constant-values';
import type HotelParityListController from 'lumen/controllers/hotel/parity/list';
import type { OtaHotelInfo, ParityConfig, Period } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import type {
  MealTypeValues,
  MembershipType,
  RateParam,
} from 'lumen/utils/constants/general-constants';
import {
  APIGATEWAY,
  MEMBERSHIPTYPES,
} from 'lumen/utils/constants/general-constants';
import type { CountryCodeWithDefault } from 'lumen/utils/constants/pi-constant-values';
import { updateDisconnectedOtas } from 'lumen/utils/disconnected-otas';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import ConfigResource from 'lumen/utils/parity/resources/config';
import OtaHotelInfoResource from 'lumen/utils/parity/resources/ota-hotel-info';
import { mapRateParamToAPIParams } from 'lumen/utils/rates-request';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

type ParityParams = {
  OTAGroup: string;
  extractDate: string;
  los: number;
  maxPersons: number;
  mealPlan: MealTypeValues;
  membershipType: MembershipType;
  month: string;
  parityAnalyticsView: boolean;
  platform: Platform;
  pos: CountryCodeWithDefault;
  rateParam: RateParam;
  roomType: string;
  showMemberRate: boolean;
};

export interface ParityRequestParams {
  base_membershiptype: MembershipType;
  extract_date?: string;
  from_date_range_end: string;
  from_date_range_start: string;
  hotel_id: string;
  los: number;
  mealtype: MealTypeValues;
  membershiptype: MembershipType;
  meta: 'nested';
  namespace: typeof APIGATEWAY;
  ota?: string[];
  persons: number;
  platform: Platform;
  pos: CountryCodeWithDefault;
  roomtype: string;
  typeKey: 'parity' | 'parities/analytics';
}

type ParityListModel = {
  config: Task<ParityConfig | null, []>;
  otaHotelInfos: Task<OtaHotelInfo[] | null, []>;
  periods: TaskInstance<Period[]>;
  roomtypes: TaskInstance<Roomtype[]>;
};

export default class HotelParityListRoute extends Route {
  queryParams = {
    rateParam: { refreshModel: true },
    los: { refreshModel: true },
    maxPersons: { refreshModel: true },
    roomType: { refreshModel: true },
    membershipType: { refreshModel: true },
    mealPlan: { refreshModel: true },
    month: { refreshModel: true },
    parityAnalyticsView: { refreshModel: true },
    pos: { refreshModel: true },
    OTAGroup: { refreshModel: false },
    platform: { refreshModel: true },
    showMemberRate: { refreshModel: true },
    extractDate: { refreshModel: true },
  };

  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare liveupdate: Services['liveupdate'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare tab: Services['tab'];
  @service declare transition: Services['transition'];

  configResource = new ConfigResource(getOwner(this)!, {
    isSingleProperty: true,
  });
  lastActiveHotelId: string | null = null;
  otaHotelInfoResource = new OtaHotelInfoResource(getOwner(this)!);
  // eslint-disable-next-line sort-class-members/sort-class-members
  modelObject = trackedObject<ParityListModel>({
    config: this.configResource.fetchResourceTask,
    otaHotelInfos: this.otaHotelInfoResource.fetchResourceTask,
    periods: {
      isError: false,
      isRunning: false,
      value: [] as Period[] | null,
    } as TaskInstance<Period[]>,
    roomtypes: {
      isError: false,
      isRunning: false,
      value: [] as Roomtype[] | null,
    } as TaskInstance<Roomtype[]>,
  });

  requestPeriodsTask = task(
    { restartable: true },
    async (requestParams: ParityRequestParams) => {
      try {
        const data = await this.store.query<Period>('period', {
          ...requestParams,
        });

        updateDisconnectedOtas(
          (data.meta?.['disconnected_otas'] as string[]) ?? [],
          this.store,
        );

        return [...data];
      } catch (error) {
        void this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  requestRoomtypesTask = task(
    { restartable: true },
    async ({ hotel_id }: { hotel_id: string }) => {
      try {
        const data = await this.store.query<Roomtype>('roomtype', {
          namespace: APIGATEWAY,
          hotel_id,
          ota: 'allchannels',
          typeKey: 'roomtypes/parity',
          data_type: 'parity',
        });
        return [...data];
      } catch (error) {
        void this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  activate(): void {
    this.liveupdate.parityTabOpen = true;
  }

  beforeModel(transition: Transition): void {
    if (!this.tab.hasParityInsight && !this.access.hasParityAnalyticsStaff) {
      this.transition.redirectToEnabledTab(transition);
    } else if (!this.hasOtasForParity()) {
      transition.abort();
      this.router.transitionTo(
        'hotel.error',
        this.hotelAndMarketService.hotel,
        { queryParams: { reason: 'no_major_otas_configured' } },
      );
    } else {
      this.transition.validateParityListQueryParams(
        this.paramsFor(this.routeName),
      );
    }
  }

  deactivate(): void {
    /* eslint-disable ember/no-controller-access-in-routes */
    (
      this.controllerFor('hotel.parity.list') as HotelParityListController
    ).parityAnalyticsView = false;
    /* eslint-enable ember/no-controller-access-in-routes */

    this.liveupdate.parityTabOpen = false;
  }

  hasOtasForParity(): boolean {
    const { OTAsParity } = this.hotelAndMarketService.hotel;
    const hasMajorOTAParity = OTAsParity.some((ota) => ota.isMajor);
    return hasMajorOTAParity || this.access.hasParityAnalyticsStaff;
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async model(params: ParityParams): Promise<ParityListModel> {
    const month = dayjs.utc(params.month, 'YYYY-MM');
    const startOfMonth = month.startOf('month');
    const today = this.hotelAndMarketService.getLocalTime();
    const dateRangeStart = today.isAfter(startOfMonth)
      ? today.format('YYYY-MM-DD')
      : startOfMonth.format('YYYY-MM-DD');
    const dateRangeEnd = month
      .endOf('month')
      .startOf('day')
      .format('YYYY-MM-DD');

    const requestPeriods: ParityRequestParams = {
      namespace: APIGATEWAY,
      typeKey: 'parity',
      hotel_id: this.hotelAndMarketService.hotel.id!,
      from_date_range_start: dateRangeStart,
      from_date_range_end: dateRangeEnd,
      mealtype: params.mealPlan,
      los: params.los,
      persons: params.maxPersons,
      pos: params.pos,
      roomtype: params.roomType,
      membershiptype: params.membershipType,
      platform: params.platform,
      meta: 'nested',
      base_membershiptype: params.showMemberRate
        ? MEMBERSHIPTYPES.MEMBER_ONLY
        : MEMBERSHIPTYPES.PUBLIC,
    };

    mapRateParamToAPIParams(params.rateParam, requestPeriods);

    // Staff-only: when set, fetches parities as of this historical shop date
    // instead of the latest.
    if (params.extractDate) {
      requestPeriods.extract_date = params.extractDate;
    }

    if (this.lastActiveHotelId !== this.hotelAndMarketService.hotel.id) {
      void this.modelObject.config.perform(); // Fetch config once on first page load
      void this.modelObject.otaHotelInfos.perform(); // Fetch otaHotelInfos once on first page load
    }
    this.lastActiveHotelId = this.hotelAndMarketService.hotel.id;

    // Exception for PA-only hotels, they should make PA parities request
    if (this.access.hasParityAnalyticsStaff && params.parityAnalyticsView) {
      requestPeriods.typeKey = 'parities/analytics';
      // Filter on ota until BE stops returning Platform unspecified (RATE-888)
      requestPeriods.ota = this.hotelAndMarketService.hotel
        .getParityAnalyticsOTAs(params.platform)
        .map((ota) => ota.id!);
    }

    // Perform roomtype request
    // Blocking for roomtype filter and roomtype mapping modal
    this.store.unloadAll('roomtype');
    this.modelObject.roomtypes = this.requestRoomtypesTask.perform({
      hotel_id: this.hotelAndMarketService.hotel.id!,
    });

    // Perform period request
    // Blocking for entire Parity tab except roomtype filter and roomtype mapping modal
    if (requestPeriods.hotel_id) {
      this.modelObject.periods =
        this.requestPeriodsTask.perform(requestPeriods);
    }

    this.liveupdate.resetPropertiesToDefault();
    setProperties(this.liveupdate, {
      currentHotelId: this.hotelAndMarketService.hotel.id!,
      rateParam: params.rateParam,
      los: params.los,
      maxPersons: params.maxPersons,
      roomtype: params.roomType,
      mealtype: params.mealPlan,
      month: params.month,
      membershipType: params.membershipType,
      isParityAnalytics: params.parityAnalyticsView,
      platform: params.platform,
      selectedOTA: '',
      compsetId: NaN,
      pos: params.pos,
      showMemberRate: params.showMemberRate,
    });

    return this.modelObject;
  }
}
